import React from 'react';
import { Element, Node, Text, Editor, Point, Range, Transforms, createEditor } from 'slate';
import { ReactEditor, withReact } from 'slate-react';
import { jsx } from 'slate-hyperscript'
import sanitizeHtml from 'sanitize-html';

import { withCustomHistory } from './custom-slate-history';
import { convertImgToBase64URL } from './helpers';
import { corsFree } from './url';
import { DOC_STYLES } from 'constants/doc';

import IconFormatHeadingOne from '../icons/icon-format-heading-one';
import IconFormatHeadingTwo from '../icons/icon-format-heading-two';
import IconFormatHeadingThree from '../icons/icon-format-heading-three';
import IconFormatLineSeparator from '../icons/icon-format-line-separator';
import IconFormatBold from '../icons/icon-format-bold';
import IconFormatItalic from '../icons/icon-format-italic';
import IconFormatUnderline from '../icons/icon-format-underline';
import IconFormatStrike from '../icons/icon-format-strike';
import IconLink from '../icons/icon-link';
import IconFormatImage from '../icons/icon-format-image';
import IconFormatTable from '../icons/icon-format-table';
import IconFormatUnorderedList from '../icons/icon-format-unordered-list';
import IconFormatOrderedList from '../icons/icon-format-ordered-list';
import IconFormatBlockquote from '../icons/icon-format-blockquote';
import IconFormatTodo from '../icons/icon-format-todo';
import IconFormatCode from '../icons/icon-format-code';
import IconDiscussionAlt from '../icons/icon-discussion-alt';

const IMG_REGEX = /<img src="(http.*?)"/g;

const SANITIZE_OPTIONS = {
    ...sanitizeHtml.defaults,
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img'],
    allowedAttributes: {
        ...sanitizeHtml.defaults.allowedAttributes,
        img: ['src', 'alt']
    }
};

export const NODE_TYPES = {
    paragraph: 'paragraph',
    headingOne: 'heading-one',
    headingTwo: 'heading-two',
    headingThree: 'heading-three',
    lineSeparator: 'line-separator',
    image: 'image',
    embed: 'embed',
    table: 'table',
    unorderedList: 'unordered-list',
    orderedList: 'ordered-list',
    blockQuote: 'block-quote',
    checklistItem: 'checklist-item',
    codeBlock: 'code-block',
    tableRow: 'table_row',
    tableCell: 'table_cell',
    listItem: 'list-item',
    deprecatedListItem: 'deprecated-list-item',
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'strikethrough',
    link: 'link',
    code: 'code',
    comment: 'comment'
};

const NON_CHILDREN_NODES = ['TABLE', 'COLGROUP', 'COL', 'TBODY', 'THEAD', 'TR', 'UL', 'OL'];

const NODES = [
    {
        id: 'paragraph',
        type: NODE_TYPES.paragraph,
        group: 'block',
        label: 'Paragraph',
        tag: 'p'
    },
    {
        id: 'headingOne',
        type: NODE_TYPES.headingOne,
        group: 'block',
        label: 'Heading 1',
        icon: <IconFormatHeadingOne />,
        tag: 'h1'
    },
    {
        id: 'headingTwo',
        type: NODE_TYPES.headingTwo,
        group: 'block',
        label: 'Heading 2',
        icon: <IconFormatHeadingTwo />,
        tag: 'h2'
    },
    {
        id: 'headingThree',
        type: NODE_TYPES.headingThree,
        group: 'block',
        label: 'Heading 3',
        icon: <IconFormatHeadingThree />,
        tag: 'h3'
    },
    {
        id: 'lineSeparator',
        type: NODE_TYPES.lineSeparator,
        group: 'block',
        label: 'Line Separator',
        icon: <IconFormatLineSeparator />,
        tag: 'hr'
    },
    {
        id: 'image',
        type: NODE_TYPES.image,
        group: 'block',
        label: 'Image',
        icon: <IconFormatImage />,
        tag: 'img'
    },
    {
        id: 'embed',
        type: NODE_TYPES.embed,
        group: 'block',
        label: 'Embed',
        icon: <IconFormatImage />,
        tag: 'iframe'
    },
    {
        id: 'table',
        type: NODE_TYPES.table,
        group: 'block',
        label: 'Table',
        icon: <IconFormatTable />,
        tag: 'table'
    },
    {
        id: 'tableRow',
        type: NODE_TYPES.tableRow,
        group: 'block',
        label: 'Table Row',
        tag: 'tr'
    },
    {
        id: 'tableCell',
        type: NODE_TYPES.tableCell,
        group: 'block',
        label: 'Table Cell',
        tag: 'td'
    },
    {
        id: 'unorderedList',
        type: NODE_TYPES.unorderedList,
        group: 'block',
        label: 'Bulleted List',
        icon: <IconFormatUnorderedList />,
        tag: 'ul'
    },
    {
        id: 'orderedList',
        type: NODE_TYPES.orderedList,
        group: 'block',
        label: 'Ordered List',
        icon: <IconFormatOrderedList />,
        tag: 'ol'
    },
    {
        id: 'blockQuote',
        type: NODE_TYPES.blockQuote,
        group: 'block',
        label: 'Quote',
        icon: <IconFormatBlockquote />,
        tag: 'blockquote'
    },
    {
        id: 'codeBlock',
        type: NODE_TYPES.codeBlock,
        group: 'block',
        label: 'Code',
        icon: <IconFormatCode />,
        tag: 'pre'
    },
    {
        id: 'listItem',
        type: NODE_TYPES.listItem,
        group: 'block',
        label: 'List Item',
        tag: 'li'
    },
    {
        id: 'checklistItem',
        type: NODE_TYPES.checklistItem,
        group: 'block',
        label: 'To-do',
        icon: <IconFormatTodo />,
        tag: 'li'
    },
    {
        id: 'link',
        type: NODE_TYPES.link,
        group: 'block',
        label: 'Link',
        inline: true,
        icon: <IconLink />,
        tag: 'a'
    },
    {
        id: 'comment',
        type: NODE_TYPES.comment,
        group: 'block',
        label: 'Comment',
        inline: true,
        icon: <IconDiscussionAlt />,
        tag: 'span'
    },
    {
        id: 'bold',
        type: NODE_TYPES.bold,
        group: 'mark',
        label: 'Bold',
        icon: <IconFormatBold />,
        tag: 'strong'
    },
    {
        id: 'italic',
        type: NODE_TYPES.italic,
        group: 'mark',
        label: 'Italic',
        icon: <IconFormatItalic />,
        tag: 'em'
    },
    {
        id: 'underline',
        type: NODE_TYPES.underline,
        group: 'mark',
        label: 'Underline',
        icon: <IconFormatUnderline />,
        tag: 'u'
    },
    {
        id: 'strikethrough',
        type: NODE_TYPES.strikethrough,
        group: 'mark',
        label: 'Strike',
        icon: <IconFormatStrike />,
        tag: 's'
    },
    {
        id: 'code',
        type: NODE_TYPES.code,
        group: 'mark',
        label: 'Code',
        icon: <IconFormatCode />,
        tag: 'code'
    }
];

export const DISABLED_NODES_WHEN_COLLAPSED = [NODE_TYPES.link, NODE_TYPES.comment];

const TOOLBAR_MENU_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough, NODE_TYPES.link],
    [NODE_TYPES.image, NODE_TYPES.embed, NODE_TYPES.table, NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem, NODE_TYPES.codeBlock],
    [NODE_TYPES.comment]
];
const CONTEXT_MENU_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough, NODE_TYPES.link],
    [NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem],
    [NODE_TYPES.comment]
];
const BLOCK_CHOOSER_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.image, NODE_TYPES.embed, NODE_TYPES.table, NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem, NODE_TYPES.codeBlock]
];

const TOOLBAR_MENU_PERSONAL_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough],
    [NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.codeBlock]
];
const CONTEXT_MENU_PERSONAL_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough],
    [NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote]
];
const BLOCK_CHOOSER_PERSONAL_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.codeBlock]
];

const TOOLBAR_MENU_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];
const CONTEXT_MENU_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];
const BLOCK_CHOOSER_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];

const TOOLBAR_MENU_VIEW_ITEMS = [];
const CONTEXT_MENU_VIEW_ITEMS = [];
const BLOCK_CHOOSER_VIEW_ITEMS = [];

const TAGS_REMAP_MAP = {
    'b': 'strong',
    'i': 'em'
}

export const getContextMenuItems = permission => {
    switch (permission) {
        case 'edit':
            return CONTEXT_MENU_EDIT_ITEMS;

        case 'comment':
            return CONTEXT_MENU_COMMENT_ITEMS;

        case 'personal':
            return CONTEXT_MENU_PERSONAL_ITEMS;

        default:
            return CONTEXT_MENU_VIEW_ITEMS;
    }
}

export const getBlockChooserItems = permission => {
    switch (permission) {
        case 'edit':
            return BLOCK_CHOOSER_EDIT_ITEMS;

        case 'comment':
            return BLOCK_CHOOSER_COMMENT_ITEMS;

        case 'personal':
            return BLOCK_CHOOSER_PERSONAL_ITEMS;

        default:
            return BLOCK_CHOOSER_VIEW_ITEMS;
    }
}

export const getToolbarMenuItems = permission => {
    switch (permission) {
        case 'edit':
            return TOOLBAR_MENU_EDIT_ITEMS;

        case 'comment':
            return TOOLBAR_MENU_COMMENT_ITEMS;

        case 'personal':
            return TOOLBAR_MENU_PERSONAL_ITEMS;

        default:
            return TOOLBAR_MENU_VIEW_ITEMS;
    }
}

export const getNodeText = node => {
    if (Text.isText(node)) {
        return node.text;
    }

    return node.children ? node.children.map(n => getNodeText(n)).join('') : '';
}

export const isPopulated = node => node?.children && Array.isArray(node.children) && node.children.some(n => Node.string(n) || isPopulated(n));

export const getNodeByType = type => NODES.find(node => node.type === type);
const getNodeByTag = tag => {
    tag = TAGS_REMAP_MAP[tag] || tag;

    return NODES.find(node => node.tag === tag);
};

const withTables = editor => {
    const { deleteBackward, deleteForward, insertBreak, normalizeNode } = editor;

    editor.deleteBackward = unit => {
        const { selection } = editor

        if (selection && Range.isCollapsed(selection)) {
            const [el] = Editor.nodes(editor, {
                match: n => n.type
            });

            const [cell] = Editor.nodes(editor, {
                match: n => n.type === NODE_TYPES.tableCell
            });

            if (selection.anchor.offset === 0 && el && el[1]) {
                const before = Editor.before(editor, el[1]);

                try {
                    const [prevParent] = before && Editor.parent(editor, before, {
                        depth: 2
                    });

                    if (prevParent && prevParent.type === NODE_TYPES.table) {
                        return;
                    }
                } catch(e) {}
            }

            if (cell) {
                const [, cellPath] = cell;
                const start = Editor.start(editor, cellPath);

                if (Point.equals(selection.anchor, start)) {
                    return;
                }
            }

            if (el && [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.codeBlock, NODE_TYPES.listItem, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem].includes(el[0].type) && isNodeEmpty(el[0])) {
                return Transforms.removeNodes(editor, { at: el[1] });
            }
        }

        deleteBackward(unit)
    }

    editor.deleteForward = unit => {
        const { selection } = editor;

        if (selection && Range.isCollapsed(selection)) {
            const [el] = Editor.nodes(editor, {
                match: n => n.type
            });

            const [cell] = Editor.nodes(editor, {
                match: n => n.type === NODE_TYPES.tableCell
            });

            if (el) {
                const [, currentNodeEnd] = Editor.edges(editor, el[1]);

                if (selection.anchor.offset === currentNodeEnd.offset) {
                    const after = Editor.after(editor, el[1]);
                    const parentNode = after && Editor.parent(editor, after, {
                        depth: 2
                    });

                    if (!parentNode) {
                        return;
                    }

                    const [nextParent] = parentNode;

                    if (nextParent && nextParent.type === NODE_TYPES.table) {
                        return;
                    }
                }
            }

            if (cell) {
                const [, cellPath] = cell;
                const end = Editor.end(editor, cellPath);

                if (Point.equals(selection.anchor, end)) {
                    return;
                }
            }
        }

        deleteForward(unit)
    }

    editor.insertBreak = () => {
        const { selection } = editor

        if (selection) {
            const [table] = Editor.nodes(editor, { match: n => n.type === NODE_TYPES.table })

            if (table) {
                return
            }
        }

        insertBreak();
    }

    editor.normalizeNode = entry => {
        const [node, path] = entry

        // If the element is a cell, ensure its children are valid.
        if (Element.isElement(node) && node.type === NODE_TYPES.tableCell) {
            const [parent] = Editor.parent(editor, path);

            if (!Element.isElement(parent) || parent.type !== NODE_TYPES.tableRow) {
                Transforms.removeNodes(editor, { at: path });
            }
        }

        if (Element.isElement(node) && node.type === NODE_TYPES.lineSeparator) {
            if (node?.children?.length && node.children[0].text !== '') {
                Transforms.setNodes(
                    editor,
                    { type: NODE_TYPES.paragraph, data: node.data },
                    { at: path }
                );
            }
        }

        // Fall back to the original `normalizeNode` to enforce other constraints.
        normalizeNode(entry);
    }

    return editor
};

export const niftyDoc = ({ mobile, onOperation } = {}) => {
    const editor = withTables(withCustomHistory(withReact(createEditor())));
    const { normalizeNode, deleteBackward, isInline, isVoid, insertData, apply } = editor;

    const getCurrentNode = () => {
        const nodes = Editor.nodes(editor, { match: n => n.type });
        if (!nodes) {
            return;
        }

        const [ currentNodeParent ] = nodes;

        if (!currentNodeParent) {
            return;
        }

        const [ currentNode ] = currentNodeParent;

        return currentNode;
    }

    editor.isInline = element => {
        const node = getNodeByType(element.type);
        return node ? Boolean(node.inline) : isInline(element);
    };

    editor.isVoid = element => {
        return [NODE_TYPES.lineSeparator, NODE_TYPES.embed].includes(element.type) ? true : isVoid(element);
    };

    editor.normalizeNode = entry => {
        const [node, path] = entry

        // Remove lists without li elements
        if (Element.isElement(node) && [NODE_TYPES.unorderedList, NODE_TYPES.orderedList].includes(node.type)) {
            if (node.children[0]?.type === 'paragraph') {
                Transforms.unwrapNodes(editor, {
                    at: path,
                    match: n => {
                        return n === node;
                    }
                });
            }
        }
        normalizeNode(entry);
    }

    editor.deleteBackward = unit => {
        const { selection } = editor

        if (selection && Range.isCollapsed(selection)) {
            const [el] = Editor.nodes(editor, {
                match: n => n.type
            });

            const [listItem] = Editor.nodes(editor, {
                match: n => n.type === NODE_TYPES.listItem
            });

            if (selection.anchor.offset === 0 && el && el[1] && listItem && listItem[1]) {
                const before = Editor.before(editor, el[1]);

                if (listItem[1][1] === 0) {
                    const paragraphNode = {
                        type: NODE_TYPES.paragraph,
                        children: listItem[0].children || [{ text: '' }]
                    };

                    Transforms.removeNodes(editor, { at: listItem[1] });

                    Transforms.insertNodes(editor, paragraphNode, {
                        at: before ? before : [0, 0],
                        select: true
                    });
                    return;
                }
            }
        }

        deleteBackward(unit)
    }


    if (!mobile) {
        editor.insertData = data => {
            const html = data.getData('text/html');

            const { selection } = editor;

            if (selection) {
                const [table] = Editor.nodes(editor, { match: n => n.type === NODE_TYPES.table });

                if (table) {
                    Transforms.insertText(editor, (data.getData('text/plain') || '').trim());

                    return;
                }
            }

            if (html) {
                let sanitizedHtml = sanitizeHtml(html, SANITIZE_OPTIONS).replace(/^((\r\n)|\n)+/, '').replace(/((\r\n)|\n)+$/, '');

                const parsed = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
                const fragment = deserialize(parsed.body).filter(node => node?.text !== '\n').map(node => {
                    if (node?.children?.length === 1) {
                        if (node.children[0]?.text === '\n') {
                            node.children[0].text = '';
                        }
                    }

                    if (node?.type === 'paragraph' && node.children?.length) {
                        if (node.children[0].text && node.children[0].text.indexOf('·      \n') === 0) {
                            node.children[0].text = node.children[0].text.replace('·      \n', '· ');
                        }
                    }

                    if (!node?.type && node.text) {
                        return {
                            type: NODE_TYPES.paragraph,
                            children: [node]
                        };
                    }

                    return node;
                }).filter(node => {
                    if (!node) {
                        return false;
                    }

                    if (!node.type && node.text === '\n\n') {
                        return false;
                    }

                    return true;
                });

                const currentNode = getCurrentNode();

                const path = currentNode && ReactEditor.findPath(editor, currentNode);
                const isEmpty = currentNode && isNodeEmpty(currentNode);

                if (isEmpty) {
                    const genericNode = getNodeByType(fragment[0].type);
                    if (fragment.length === 1 && fragment[0].type && genericNode?.group !== 'block' && genericNode?.block !== NODE_TYPES.paragraph) {
                        Transforms.removeNodes(editor, { at: path });
                    }

                    requestAnimationFrame(() => {
                        Transforms.insertFragment(editor, fragment, { select: true });
                    });
                } else {
                    Transforms.insertFragment(editor, fragment);
                }
                return;
            }

            insertData(data);
        };
    }

    editor.apply = op => {
        onOperation && onOperation(op);
        apply(op);
    };

    editor.originalApply = apply;

    return editor;
}

export const isNodeEmpty = node => {
    const selfEmpty = !node.text || node.text === '' || /^\s+$/.test(node.text);

    if (!selfEmpty) {
        return false;
    }

    if (!node.children?.length) {
        return true;
    }

    return node.children.every(child => isNodeEmpty(child));
};

export const getNodesByType = (editor, types) => {
    if (!editor.children.length) {
        return [];
    }

    if (typeof types === 'string') {
        types = [types];
    }

    const [...nodes] = Editor.nodes(editor, {
        at: [],
        match: n => types.includes(n.type)
    });

    return nodes;
}

export const isMarkActive = (editor, type) => {
    const marks = Editor.marks(editor);
    return marks ? marks[type] === true : false;
}

export const isBlockActive = (editor, type) => {
    const [match] = Editor.nodes(editor, {
        match: n => n.type === type
    });
    return !!match;
}

export const createTableCell = () => ({
    type: NODE_TYPES.tableCell,
    children: [{ text: '' }]
});

export const createTableRow = cols => {
    const row = {
        type: NODE_TYPES.tableRow,
        children: []
    };

    for (let i = 0; i < cols; i++) {
        const cell = createTableCell();

        row.children.push(cell);
    }

    return row;
};

export const createTableNode = (rows = 2, cols = 3) => {
    const node = {
        type: NODE_TYPES.table,
        children: []
    };

    for (let i = 0; i < rows; i++) {
        node.children.push(createTableRow(cols));
    }

    return node;
}

export const serialize = node => {
    if (Text.isText(node)) {
        return node.text;
    }

    const children = node.children ? node.children.map(n => serialize(n)).join('') : '';

    const nodeProps = getNodeByType(node.type);

    if (!nodeProps) {
        return children;
    }

    switch (node.type) {
        case NODE_TYPES.image:
            return `<p><img src="${node.data.fileUrl}" alt="${children}" /></p>`;

        case NODE_TYPES.embed:
            return `<p>Embed: ${node.data.url}</p>`;

        case NODE_TYPES.checklistItem:
            return `<li><input type="checkbox" checked="${node.data && node.data.checked}" /> ${children}</li>`;

        case NODE_TYPES.link:
            return `<a href=${node.data && node.data.href}>${children}</a>`;

        default:
            return `<${nodeProps.tag}>${children}</${nodeProps.tag}>`;
    }
}

const filterNonTextParagraphs = nodes => nodes.filter(node => node && (node.type !== NODE_TYPES.paragraph || node.children.length));
const filterNonListNodesInLists = nodes => {
    return nodes.map(node => {
        if (node.type === NODE_TYPES.paragraph) {
            node.type = NODE_TYPES.listItem;
        }

        return node;
    }).filter(node => node && node.type === NODE_TYPES.listItem);
};

export const deserialize = el => {
    if (['TABLE', 'TBODY', 'TR'].includes(el.nodeName) && el.innerText.trim() === '') {
        return null;
    }

    if (el.nodeName === 'TABLE' && el.querySelectorAll('table').length) {
        return [...el.querySelectorAll('table')].map(table => {
            const childTables = [...table.querySelectorAll('table')];

            childTables.forEach(t => t.parentNode.removeChild(t));

            return deserialize(table);
        });
    }

    if (typeof el === 'string') {
        return el;
    } else if (el.nodeType === 3) {
        return el.textContent;
    } else if (el.nodeType !== 1) {
        return null;
    } else if (el.nodeName === 'BR') {
        return '\n';
    }

    const { nodeName } = el;
    let parent = el;

    if (
        nodeName === 'PRE' &&
        el.childNodes[0] &&
        el.childNodes[0].nodeName === 'CODE'
    ) {
        parent = el.childNodes[0];
    }

    let flatChildren = [];

    if (NON_CHILDREN_NODES.includes(nodeName)) {
        flatChildren = Array.from(parent.children).map(deserialize).flat();
    } else {
        flatChildren = Array.from(parent.childNodes).map(deserialize).flat();
    }

    let children = filterNonTextParagraphs(flatChildren);

    if (parent.nodeName === 'UL' || parent.nodeName === 'OL') {
        children = filterNonListNodesInLists(children);
    }

    if (el.nodeName === 'BODY') {
        return jsx('fragment', {}, children);
    }

    if (el.nodeName === 'SPAN') {
        return children;
    }

    const node = getNodeByTag(nodeName.toLowerCase());

    if (node) {
        const attrs = {
            type: node.type,
            data: {}
        }

        switch (node.type) {
            case NODE_TYPES.link:
                attrs.data.href = el.getAttribute('href');
                return jsx('element', attrs, children);

            case NODE_TYPES.image:
                attrs.data.fileUrl = el.getAttribute('src');
                return jsx('element', attrs, [el.getAttribute('alt')]);

            case NODE_TYPES.embed:
                attrs.data.url = el.getAttribute('src');
                return jsx('element', attrs);

            case NODE_TYPES.comment:
                return children;

            case NODE_TYPES.codeBlock:
                if (el.children[0]?.nodeName !== 'CODE') {
                    return jsx('element', {
                        type: NODE_TYPES.paragraph
                    }, children);
                }
                return children;

            default:
                if (node.group === 'block') {
                    if (children.length === 0) {
                        children = [{ text: '' }];
                    }

                    return jsx('element', attrs, children);
                }

                return children.map(child => {
                    if (typeof child === 'object') {
                        const childAttrs = {
                            type: child.type,
                            data: child.data
                        }
                        return jsx('element', childAttrs, child.children);
                    } else {
                        return jsx('text', {
                            [node.type]: true
                        }, child)
                    }
                });
        }
    } else if (el.nodeName === 'DIV') {
        if (children.every(child => typeof child === 'string')) {
            return jsx('element', {
                type: NODE_TYPES.paragraph
            }, children);
        }
    }

    return children;
}

const convertDocImagesToBase64 = docHtml => {
    const urls = [];
    let matches = [];

    while (matches) {
        matches = IMG_REGEX.exec(docHtml);
        if (matches) {
            urls.push(matches[1]);
        }
    }

    const uniqueUrls = [...new Set(urls)];

    return Promise
        .all(uniqueUrls.map(url => url ? convertImgToBase64URL(corsFree(url)) : ''))
        .then(responses => {
            responses.forEach((base64, index) => {
                docHtml = docHtml.replace(new RegExp(uniqueUrls[index], 'g'), base64);
            })

            return docHtml;
        });
}

export const getDocPrintHtml = async (pages, withStyles) => {
    const parsedPages = await Promise.all(pages.map(async page => {
        const content = await convertDocImagesToBase64(serialize({ children: page.content }));

        return {
            title: page.title,
            content
        };
    }));

    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <title>${pages[0].title} | Nifty</title>
            <style>${ withStyles ? DOC_STYLES : '' }</style>
        </head>
        <body>
            <div class="doc-canvas">
                ${parsedPages.map(({ title, content }) => `
                    <div class="page">
                        <h1 class="doc-title">${title}</h1>

                        ${ content }
                    </div>
                `).join('\n')}
            </div>
        </body>
        </html>
    `;
}
